import { XimiIdentityErrorCode } from './ximiStimulinIdentity';
import { ximiMobileGetFlat } from './ximiMobileClient';
import { resolveXimiStimulinErrorCode, XIMI_ACCESS_REFUSED } from './ximiStimulinUnauthorized';

// Endpoint Ozmo (lecture seule, aucune écriture) : verdict signé « cet appareil tient-il encore
// la session Stimul'In du compte ? ». Règle produit « un appareil par compte » : le dernier
// connecté gagne. Appelé avant chaque refresh StimulinAPI, qui vérifie le jeton.
// Voir .claude/migration/analyse-sessions-multi-device.md § 8.
const STIMULIN_SESSION = 'stimulin/session';

/** Header transportant le jeton `session` vers StimulinAPI sur le refresh. */
export const XIMI_SESSION_HEADER = 'X-Ximi-Session-Token';

/**
 * `api/stimulin/*` codes meaning this device's Stimul'In session is definitively lost: the Ozmo
 * contract 403 codes (mirror — keep in sync) and the 401 that persists with a fresh SSO token.
 */
export const XimiSessionLostCode = {
    /** Un autre appareil a ouvert la session du compte depuis. */
    DEVICE_EVICTED: 'STIMULIN_DEVICE_EVICTED',
    /** Le tenant n'a plus l'option Stimul'In. */
    SUBSCRIPTION_INACTIVE: XimiIdentityErrorCode.SUBSCRIPTION_INACTIVE,
    /** Access refused even with a fresh SSO token (unknown account or disabled access). */
    ACCESS_REFUSED: XIMI_ACCESS_REFUSED
} as const;

export type XimiSessionLostCodeValue =
    (typeof XimiSessionLostCode)[keyof typeof XimiSessionLostCode];

const SESSION_LOST_CODES: readonly string[] = Object.values(XimiSessionLostCode);

/** `true` si ce code Ozmo signifie une session perdue (déconnexion obligatoire). */
export const isXimiSessionLostCode = (code: unknown): code is XimiSessionLostCodeValue =>
    typeof code === 'string' && SESSION_LOST_CODES.includes(code);

export type XimiSessionProofResult =
    /** Jeton signé à poser sur le refresh. */
    | { ok: true; token: string }
    /** Session perdue : l'appareil doit se déconnecter. */
    | { ok: false; lost: true; code: XimiSessionLostCodeValue }
    /** Route indisponible (panne, réseau) : pas de refresh, la session est conservée. */
    | { ok: false; lost: false; reason: string };

/** Demande le verdict de session à Ozmo. Ne lève jamais. */
export async function fetchXimiStimulinSessionProof(): Promise<XimiSessionProofResult> {
    try {
        const { status, data } = await ximiMobileGetFlat<{ token?: string; code?: string }>(
            STIMULIN_SESSION
        );
        if (status < 200 || status >= 300) {
            const code = resolveXimiStimulinErrorCode(status, data?.code);
            return isXimiSessionLostCode(code)
                ? { ok: false, lost: true, code }
                : { ok: false, lost: false, reason: `HTTP ${status}` };
        }
        return data?.token
            ? { ok: true, token: data.token }
            : { ok: false, lost: false, reason: 'No token in response' };
    } catch (error) {
        return {
            ok: false,
            lost: false,
            reason: error instanceof Error ? error.message : 'Network error'
        };
    }
}
